// Comparação de textos de proposta para o Histórico (15/09/2026). Sem dependência nova:
// o HTML vira parágrafos e a comparação é por parágrafo (maior subsequência comum).
// Serve para a pessoa VER o que muda ao restaurar — não para gravar nada.

import { rotuloDaArea } from './areasEmpresa';

export type TrechoDiff = { tipo: 'igual' | 'removido' | 'adicionado'; texto: string };

const ENTIDADES: Record<string, string> = {
  '&nbsp;': ' ', '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&apos;': "'",
};

/** HTML → lista de parágrafos com o texto visível (sem tags, sem linhas vazias). */
export function htmlParaBlocos(html: string | null | undefined): string[] {
  if (!html) return [];
  const comQuebras = String(html)
    .replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, ' ')
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*\/\s*(p|li|h[1-6]|tr|div|blockquote|ul|ol|table)\s*>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&(nbsp|amp|lt|gt|quot|#39|apos);/g, m => ENTIDADES[m] ?? m);
  return comQuebras
    .split('\n')
    .map(l => l.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

// Ordem e rótulo dos campos de um item de venda ou de uma parcela, na linha comparada.
const CAMPOS_DE_LINHA: [string, string][] = [
  ['descricao', ''], ['nome', ''], ['quantidade', 'Qtd.'], ['unidade', ''], ['valor_unitario', 'Valor unit.'],
  ['valor_total', 'Total'], ['percentual', '%'], ['valor_str', 'Valor'], ['prazo', 'Prazo'],
  ['dias_apos_assinatura', 'Dias após assinatura'], ['prazo_pagamento_dias', 'Prazo de pagamento (dias)'],
];

function textoSimples(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * Itens de venda prontos para a comparação que a pessoa lê (05/10/2026): a área
 * pelo NOME (a linha guarda a chave, `topografia`) e a sub-área pelo nome, quando
 * quem chama tem a lista (a linha guarda o identificador). Sem a lista, o
 * identificador fica — sumir com ele esconderia a mudança.
 */
export function itensParaLeitura(itens: unknown, nomeDaSubarea?: (id: string) => string | undefined): unknown {
  if (!Array.isArray(itens)) return itens;
  return itens.map(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    const { setor_chave, subarea_id, ...resto } = item as Record<string, unknown>;
    const saida: Record<string, unknown> = { ...resto };
    if (setor_chave) saida.setor_chave = rotuloDaArea(String(setor_chave)) || String(setor_chave);
    if (subarea_id) saida.subarea_id = (nomeDaSubarea && nomeDaSubarea(String(subarea_id))) || String(subarea_id);
    return saida;
  });
}

/**
 * Um item de venda, uma parcela etc. numa linha só, com TODOS os campos preenchidos —
 * campo que não aparece na linha seria mudança invisível na comparação.
 */
export function linhaDeObjeto(o: Record<string, unknown>): string {
  const partes: string[] = [];
  const usados = new Set<string>();
  for (const [chave, rotulo] of CAMPOS_DE_LINHA) {
    if (!(chave in o)) continue;
    usados.add(chave);
    const t = textoSimples(o[chave]);
    if (!t) continue;
    if (chave === 'percentual') partes.push(`${t}%`);
    else if (chave === 'valor_str') partes.push(`R$ ${t}`);
    else partes.push(rotulo ? `${rotulo} ${t}` : t);
  }
  for (const chave of Object.keys(o).sort()) {
    if (usados.has(chave)) continue;
    const t = textoSimples(o[chave]);
    if (t) partes.push(`${rotuloCampo(chave)}: ${t}`);
  }
  return partes.join(' · ') || '(vazio)';
}

/** Valor de campo (texto, número, objeto, lista) → parágrafos comparáveis. */
export function valorParaBlocos(valor: unknown): string[] {
  if (valor === null || valor === undefined) return [];
  if (typeof valor === 'string') return /<[a-z][\s\S]*>/i.test(valor) ? htmlParaBlocos(valor) : valor.split('\n').map(l => l.trim()).filter(Boolean);
  if (Array.isArray(valor)) {
    return valor.map((v, i) => (typeof v === 'object' && v !== null && !Array.isArray(v)
      ? `${i + 1}. ${linhaDeObjeto(v as Record<string, unknown>)}`
      : textoSimples(v)));
  }
  if (typeof valor === 'object') {
    return Object.entries(valor as Record<string, unknown>)
      .filter(([, v]) => textoSimples(v) !== '')
      .map(([k, v]) => `${rotuloCampo(k)}: ${textoSimples(v)}`);
  }
  return [String(valor)];
}

const TETO_CELULAS = 250_000;

/**
 * antes → depois, parágrafo a parágrafo. `removido` = só no antes; `adicionado` = só no depois.
 * Acima de ~250 mil comparações (textos enormes) devolve tudo como troca e marca `aproximado`.
 */
export function diffBlocos(antes: string[], depois: string[]): { trechos: TrechoDiff[]; aproximado: boolean } {
  const n = antes.length;
  const m = depois.length;
  if ((n + 1) * (m + 1) > TETO_CELULAS) {
    return {
      trechos: [
        ...antes.map(texto => ({ tipo: 'removido' as const, texto })),
        ...depois.map(texto => ({ tipo: 'adicionado' as const, texto })),
      ],
      aproximado: true,
    };
  }
  // tabela[i][j] = tamanho da maior subsequência comum de antes[i..] e depois[j..]
  const tabela: Uint32Array[] = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      tabela[i][j] = antes[i] === depois[j] ? tabela[i + 1][j + 1] + 1 : Math.max(tabela[i + 1][j], tabela[i][j + 1]);
    }
  }
  const trechos: TrechoDiff[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (antes[i] === depois[j]) { trechos.push({ tipo: 'igual', texto: antes[i] }); i++; j++; }
    else if (tabela[i + 1][j] >= tabela[i][j + 1]) { trechos.push({ tipo: 'removido', texto: antes[i] }); i++; }
    else { trechos.push({ tipo: 'adicionado', texto: depois[j] }); j++; }
  }
  while (i < n) trechos.push({ tipo: 'removido', texto: antes[i++] });
  while (j < m) trechos.push({ tipo: 'adicionado', texto: depois[j++] });
  return { trechos, aproximado: false };
}

/**
 * Chaves de topo cujo conteúdo difere entre dois objetos (dados estruturados).
 * `ignorar`: chaves já mostradas em outro lugar (o título tem linha própria).
 */
export function camposDiferentes(
  a: Record<string, unknown> | null | undefined,
  b: Record<string, unknown> | null | undefined,
  ignorar: string[] = [],
): string[] {
  const x = a || {};
  const y = b || {};
  const chaves = Array.from(new Set([...Object.keys(x), ...Object.keys(y)])).filter(k => !ignorar.includes(k));
  return chaves.filter(k => JSON.stringify(x[k] ?? null) !== JSON.stringify(y[k] ?? null)).sort();
}

const ROTULOS: Record<string, string> = {
  titulo: 'Título',
  conteudo_final: 'Documento montado',
  dados_estruturados: 'Textos e condições',
  itens_venda: 'Serviços e valores',
  composicao_id: 'Composição vinculada',
  revisao: 'Número da revisão',
  descricao_tecnica: 'Descrição técnica',
  entregaveis: 'Entregáveis',
  cronograma: 'Cronograma',
  condicoes_gerais: 'Condições gerais',
  condicoes_pagamento: 'Condições de pagamento',
  forma_pagamento: 'Forma de pagamento',
  parcelas: 'Parcelas',
  medicoes: 'Medições',
  prazo_entrega: 'Prazo de entrega',
  prazo_total: 'Prazo total',
  responsabilidades_contratada: 'Responsabilidades da contratada',
  responsabilidades_contratante: 'Responsabilidades do contratante',
  itens_nao_integram: 'Itens que não integram',
  informacoes_complementares: 'Informações complementares',
  empresa_contratada: 'Empresa contratada',
  desconto_geral_pct: 'Desconto geral',
  objeto: 'Objeto',
  escopo: 'Escopo',
  metodologia: 'Metodologia',
  premissas: 'Premissas',
  validade: 'Validade da proposta',
  observacoes: 'Observações',
  referencia: 'Referência',
  cliente: 'Cliente',
  contato: 'Contato',
  local: 'Local',
  area: 'Área',
  garantia: 'Garantia',
  reajuste: 'Reajuste',
  impostos: 'Impostos',
  valor_total: 'Valor total',
  valor_global: 'Valor global',
  total_venda: 'Total da venda',
  descricao: 'Descrição',
  quantidade: 'Quantidade',
  unidade: 'Unidade',
  valor_unitario: 'Valor unitário',
  percentual: 'Percentual',
  valor_str: 'Valor',
  prazo: 'Prazo',
  grupo: 'Grupo',
  servico_id: 'Serviço (id)',
  setor_chave: 'Área',
  subarea_id: 'Sub-área',
  cnae: 'Código da NF',
  desconto_pct: 'Desconto (%)',
  servico_nome: 'Serviço',
  dias_apos_assinatura: 'Dias após assinatura',
  prazo_pagamento_dias: 'Prazo de pagamento (dias)',
  fixada: 'Fixada',
};

export function rotuloCampo(chave: string): string {
  return ROTULOS[chave] || chave.replace(/_/g, ' ');
}

export function rotuloOrigem(origem: string | null | undefined, autor?: string | null): string {
  const quem = autor ? autor.split('@')[0] : '';
  switch (origem) {
    case 'dwight': return quem ? `Dwight (card aprovado por ${quem})` : 'Dwight';
    case 'tela': return quem || 'Tela';
    case 'restauracao': return quem ? `Restauração por ${quem}` : 'Restauração';
    case 'desfazer': return quem ? `Desfazer por ${quem}` : 'Desfazer';
    default: return 'Sistema (atualização automática)';
  }
}
